/**
 * OTP — Carrinho flutuante.
 *
 * Scoped to <otp-floating-cart>. Touches nothing else on the page.
 *
 * Two jobs:
 *   1. show the button only once the header's own cart icon has scrolled out of
 *      view, so there is never a second cart icon while the header is visible;
 *   2. on product pages, lift the button above the sticky add-to-cart bar
 *      whenever that bar is showing and would sit underneath it.
 *
 * The count badge itself is the theme's own <cart-icon>, which listens to the
 * same cart events as the header icon — nothing to do here.
 */

class OtpFloatingCart extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';

    this.observeHeaderIcon();
    this.trackStickyBar();
  }

  /* ---------- 1. visibility follows the header icon ---------- */
  observeHeaderIcon() {
    const headerIcon = document.querySelector('header-actions .header-actions__cart-icon');

    /* no header icon to mirror: just show the button */
    if (!headerIcon || !('IntersectionObserver' in window)) {
      this.setVisible(true);
      return;
    }

    this.headerObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry) return;
        this.setVisible(!entry.isIntersecting);
      },
      { threshold: 0 }
    );
    this.headerObserver.observe(headerIcon);
  }

  setVisible(visible) {
    this.dataset.visible = visible ? 'true' : 'false';
    this.setAttribute('aria-hidden', visible ? 'false' : 'true');
    /* keep the button out of the tab order while it is invisible */
    const button = this.querySelector('.otp-floating-cart__button');
    if (button) button.tabIndex = visible ? 0 : -1;
  }

  /* ---------- 2. stay clear of the sticky add-to-cart bar ---------- */
  trackStickyBar() {
    const bar = document.querySelector('.sticky-add-to-cart__bar');
    if (!bar) return;

    const gap = 12;
    const edge = parseFloat(getComputedStyle(this).getPropertyValue('--otp-floating-cart-edge')) || 16;
    const size = parseFloat(getComputedStyle(this).getPropertyValue('--otp-floating-cart-size')) || 52;

    const update = () => {
      const stuck = bar.getAttribute('data-stuck') === 'true';
      if (!stuck) {
        this.style.removeProperty('--otp-floating-cart-offset');
        return;
      }

      /* The bar slides in with a transform, so read its resting place from the
         layout values instead of the animated box: CSS `bottom` + own height. */
      const barBottom = parseFloat(getComputedStyle(bar).bottom) || 0;
      const barHeight = bar.offsetHeight;

      /* On wide screens the bar is centred and narrower than the page, so only
         lift the button when the bar actually reaches under it. */
      const barRight = bar.getBoundingClientRect().right;
      const buttonLeft = window.innerWidth - edge - size - gap;
      if (barRight <= buttonLeft) {
        this.style.removeProperty('--otp-floating-cart-offset');
        return;
      }

      this.style.setProperty('--otp-floating-cart-offset', `${barBottom + barHeight + gap}px`);
    };

    this.barObserver = new MutationObserver(update);
    this.barObserver.observe(bar, { attributes: true, attributeFilter: ['data-stuck'] });
    window.addEventListener('resize', update, { passive: true });
    this.stopResize = () => window.removeEventListener('resize', update);
    update();
  }

  disconnectedCallback() {
    this.headerObserver?.disconnect();
    this.barObserver?.disconnect();
    this.stopResize?.();
  }
}

if (!customElements.get('otp-floating-cart')) {
  customElements.define('otp-floating-cart', OtpFloatingCart);
}
